export const forex = {
    id: 'forex',
    title: 'Forex',
    summary: 'How currency pairs are quoted, majors and crosses, pips and what they are worth, the trading sessions, interest rates and central banks, and why swap differs between pairs.',
    level: 'intermediate',
    art: 'usd',
    image: { src: '/academy/forex.webp', alt: 'A dollar coin and a euro coin facing each other on a dark floor' },
    screen: { to: '/markets', label: 'Markets' },
    lessons: [
        {
            id: 'currency-pair-base-quote',
            title: 'How a currency pair works: base and quote',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'A currency is always priced in another currency, so forex is traded in **pairs**. In EUR/USD, the euro is the **base** currency and the US dollar is the **quote** currency.' },
                { type: 'p', text: 'The price says how much of the quote currency one unit of the base currency costs. EUR/USD at 1.1600 means one euro costs 1.16 US dollars. USD/JPY at 150.00 means one dollar costs 150 yen.' },
                {
                    type: 'compare',
                    left: { title: 'Buy EUR/USD', items: ['You buy euros and sell dollars', 'Profits if the euro gains on the dollar', 'The price goes up'] },
                    right: { title: 'Sell EUR/USD', items: ['You sell euros and buy dollars', 'Profits if the dollar gains on the euro', 'The price goes down'] },
                },
                { type: 'example', title: 'A EUR/USD trade', text: 'You buy 10,000 euros of EUR/USD at 1.1600, a trade worth $11,600. The pair rises to 1.1650 and you close. The profit is 10,000 × 0.0050 = **$50**.' },
                { type: 'p', text: 'Pairs keep their market names here, such as EUR/USD and USD/JPY. A pair like USD/JPY is priced in yen, and its profit or loss is converted to USDT at the live rate, so your balance stays in one currency.' },
            ],
            quiz: [
                {
                    q: 'In GBP/USD, which is the base currency?',
                    options: ['GBP', 'USD', 'USDT'],
                    answer: 0,
                    why: 'The first currency in a pair is the base; the price says how many dollars one pound costs.',
                },
                {
                    q: 'You think the dollar will gain on the yen. Which trade fits that view on USD/JPY?',
                    options: ['Buy', 'Sell', 'Neither'],
                    answer: 0,
                    why: 'USD is the base in USD/JPY. A stronger dollar raises the price, which a buy profits from.',
                },
            ],
            tryIt: { to: '/trade/EURUSD', label: 'Open EUR/USD' },
            terms: ['base-currency', 'quote-currency', 'long', 'short'],
        },
        {
            id: 'majors-minors-crosses',
            title: 'Majors, minors and crosses',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'The US dollar is on one side of most forex trading. The **major pairs** match the dollar with another big currency: EUR/USD, GBP/USD, USD/JPY, USD/CHF, USD/CAD, AUD/USD and NZD/USD.' },
                { type: 'p', text: 'A **cross** is a pair without the dollar, such as EUR/GBP, EUR/JPY, GBP/JPY, EUR/CHF or AUD/JPY. Crosses of the big currencies are also called **minors**. Pairs that match a big currency with one from a smaller economy are called **exotics**.' },
                {
                    type: 'list',
                    items: [
                        '**Majors** are the most traded, so their spreads are usually the tightest.',
                        '**Crosses** move on the news of two economies, without the dollar in between.',
                        'Pairs with the yen tend to react strongly when investors turn cautious, because the yen is often bought in a sell-off.',
                    ],
                },
                { type: 'example', title: 'Spread on a major and a cross', text: 'Say EUR/USD shows 1.16000 / 1.16010, a spread of 0.00010, and GBP/JPY shows 201.000 / 201.030, a spread of 0.030. On 10,000 units, that is $1 on EUR/USD, and 300 yen on GBP/JPY, about $2 at 150 yen to the dollar. The order panel shows the real bid and ask.' },
                { type: 'tip', text: 'Open the **Forex** tab on the Markets page to see every pair you can trade, with its spread and change.' },
            ],
            quiz: [
                {
                    q: 'Which of these is a cross?',
                    options: ['EUR/USD', 'EUR/GBP', 'USD/JPY'],
                    answer: 1,
                    why: 'A cross does not include the US dollar. EUR/GBP is euros priced in pounds.',
                },
                {
                    q: 'Why do major pairs usually have the tightest spreads?',
                    options: ['They are traded the most', 'They are commission-free by law', 'They only move during London hours'],
                    answer: 0,
                    why: 'More trading means more buyers and sellers close together, so the gap between bid and ask is smaller.',
                },
            ],
            tryIt: { to: '/markets', label: 'Open the Forex tab' },
            terms: ['major-pair', 'cross-pair', 'spread'],
        },
        {
            id: 'pips-and-pip-value',
            title: 'Pips and what a pip is worth',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Forex moves are counted in **pips**. For most pairs a pip is the fourth decimal place, 0.0001. For pairs priced in yen it is the second decimal place, 0.01. Prices often show one more digit, a tenth of a pip.' },
                { type: 'p', text: 'What a pip is worth depends on the trade size. 1 lot is 100,000 units of the base currency. Pip value = units × pip size, in the quote currency.' },
                { type: 'example', title: 'EUR/USD', text: '1 lot × 0.0001 = **$10 a pip**. At 0.1 lots (10,000 euros) it is $1 a pip. The pair rises from 1.1600 to 1.1625, 25 pips: +$25 at 0.1 lots, +$250 at 1 lot.' },
                { type: 'example', title: 'USD/JPY', text: '1 lot × 0.01 = 1,000 yen a pip. At 150.00 yen to the dollar that is 1,000 ÷ 150 = **$6.67 a pip**. At 0.1 lots it is about $0.67 a pip.' },
                { type: 'p', text: 'On {brand}, profit and loss on a pair priced in another currency are converted to USDT at the live rate, and fixed at the rate when you close. So a yen pip is worth a little more in dollars when the yen is stronger, and a little less when it is weaker.' },
                { type: 'tip', text: 'Work out the pip value before you place a stop. A 30-pip stop at 0.5 lots on EUR/USD is a $150 loss if it is hit.' },
            ],
            quiz: [
                {
                    q: 'On EUR/USD, what is one pip worth at 0.2 lots?',
                    options: ['$0.20', '$2', '$20'],
                    answer: 1,
                    why: '0.2 lots is 20,000 euros; 20,000 × 0.0001 = $2 a pip.',
                },
                {
                    q: 'USD/JPY moves from 150.20 to 150.50. How many pips is that?',
                    options: ['3', '30', '300'],
                    answer: 1,
                    why: 'A yen pip is 0.01, so a move of 0.30 is 30 pips.',
                },
                {
                    q: 'In which currency is the profit on a USD/JPY trade added to your balance?',
                    options: ['Yen', 'USDT, converted at the live rate', 'It stays in the trade'],
                    answer: 1,
                    why: 'Profit and loss on pairs priced in another currency are converted to USDT, your account currency.',
                },
            ],
            tryIt: { to: '/trade/EURUSD', label: 'Size a EUR/USD order in lots' },
            terms: ['pip', 'lot', 'lot-size'],
        },
        {
            id: 'forex-sessions',
            title: 'Sessions: Tokyo, London, New York',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Forex has no single exchange. It trades around the world from Sunday evening to Friday evening, New York time, and the busiest hours move with the sun from one financial centre to the next.' },
                {
                    type: 'list',
                    items: [
                        '**Sydney and Tokyo** open the week. The yen and the Australian and New Zealand dollars are most active.',
                        '**London** is the largest centre. The euro, the pound and the Swiss franc are busiest, and many daily moves begin here.',
                        '**New York** brings most US data releases. The afternoon in London, when London and New York are both open, is usually the busiest part of the day.',
                    ],
                },
                { type: 'p', text: 'The **Market pulse** at the top of the Markets page shows which sessions are open right now, counting each centre as open from 08:00 to 17:00 local time on weekdays. Between them it says **Between sessions**.' },
                { type: 'example', title: 'Range by session', text: 'Say EUR/USD moves 20 pips in the Tokyo hours and 60 pips once London and New York are both open. A 15-pip stop is wide for the quiet hours and tight for the busy ones.' },
                { type: 'tip', text: 'Spreads are often wider in the quiet hour around the New York close, and at the Sunday open. Check the bid and ask before you trade then.' },
            ],
            quiz: [
                {
                    q: 'When is forex usually busiest?',
                    options: ['On Saturday', 'When London and New York are both open', 'Between sessions'],
                    answer: 1,
                    why: 'The overlap of the two largest centres brings the most trading.',
                },
                {
                    q: 'Where can you see which forex sessions are open now?',
                    options: ['The Market pulse on the Markets page', 'The Wallet page', 'Your statements'],
                    answer: 0,
                    why: 'The Market pulse shows Sydney, Tokyo, London and New York, and which of them are open.',
                },
            ],
            tryIt: { to: '/markets', label: 'See which sessions are open' },
            terms: ['forex-session', 'trading-hours'],
        },
        {
            id: 'rates-and-central-banks',
            title: 'Interest rates and central banks',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Each currency has a **central bank** that sets its main interest rate: the Federal Reserve for the dollar, the European Central Bank for the euro, the Bank of England for the pound, the Bank of Japan for the yen, and so on.' },
                { type: 'p', text: 'Higher rates pay more to people who hold a currency, which tends to attract money. So when a central bank raises rates, or signals that it will, its currency often gains. The gap between two countries’ rates is the **interest rate differential**.' },
                { type: 'p', text: 'Markets price in what they expect long before a decision. What moves a pair on the day is the **surprise**: a decision, or a comment about the future, that is different from what was expected.' },
                { type: 'example', title: 'A rate surprise', text: 'You hold a buy of 0.2 lots of EUR/USD. The US central bank sounds more ready to raise rates than expected, the dollar gains, and the pair falls 80 pips in minutes. At $2 a pip, that is a **$160** loss.' },
                { type: 'warn', text: 'Around rate decisions, prices can jump past your stop and spreads can widen. A stop then closes at the next available price, which can be worse than the level you set.' },
                { type: 'tip', text: 'When it is available, the economic calendar on the Markets page lists rate decisions and other high-impact events. You can also set an alert for an economic event that is coming.' },
            ],
            quiz: [
                {
                    q: 'A central bank raises rates by exactly as much as everyone expected. What is most likely?',
                    options: ['A huge move, because rates went up', 'A smaller move, because it was already expected', 'The market closes'],
                    answer: 1,
                    why: 'Expected news is already in the price. Surprises are what move a pair on the day.',
                },
                {
                    q: 'What is an interest rate differential?',
                    options: ['The gap between two countries’ interest rates', 'The spread on a currency pair', 'The fee on a forex trade'],
                    answer: 0,
                    why: 'It is the difference between the rates of the two currencies in a pair.',
                },
            ],
            tryIt: { to: '/markets', label: 'Check upcoming events' },
            terms: ['central-bank', 'interest-rate-differential', 'rate-decision'],
        },
        {
            id: 'carry-and-swap',
            title: 'Carry: why swap differs between pairs',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Holding a currency pair means holding one currency and owing the other. If the currency you hold pays a higher interest rate than the one you owe, the difference works in your favour each day. This is called **carry**.' },
                { type: 'p', text: 'Here, carry shows up as **swap**, charged or paid at {swapHour}. Each pair has its own rate for buys and for sells, set by {brand}, and they can be very different. Forex is not charged at the weekend and is charged ×3 on {swapTripleDay} instead.' },
                { type: 'calc', calc: 'swap' },
                { type: 'example', title: 'A week of swap', text: 'You hold $20,000 of EUR/USD with an example buy swap of −0.01% a day. That is −$2 on a normal day and −$6 on {swapTripleDay}: **−$14** for a Monday-to-Friday week.' },
                { type: 'p', text: 'The **Swap rates** view on the Markets page lists each instrument’s swap for a long and a short, per day and on $10,000. Its **Overnight carry** column says whether one side earns swap or both sides pay.' },
                { type: 'warn', text: 'Carry is small next to price moves. Earning $1 a day on a $20,000 trade adds up to about $30 a month; a 1% move against you is $200. Never hold a trade only for its swap.' },
            ],
            quiz: [
                {
                    q: 'A forex trade pays −$2 swap on a normal day. What is charged on {swapTripleDay}?',
                    options: ['−$2', '−$4', '−$6'],
                    answer: 2,
                    why: 'Forex skips the weekend and is charged three days of swap on {swapTripleDay}: 3 × $2 = $6.',
                },
                {
                    q: 'Where can you compare swap across instruments?',
                    options: ['The Swap rates view on the Markets page', 'The Wallet page', 'Only after you close a trade'],
                    answer: 0,
                    why: 'The Swap rates view lists the long and short swap for each instrument side by side.',
                },
            ],
            tryIt: { to: '/markets', label: 'Open the swap rates' },
            terms: ['carry', 'swap', 'triple-swap'],
        },
    ],
};
export const forexTerms = [
    { id: 'base-currency', term: 'Base currency', text: 'The first currency in a pair, such as EUR in EUR/USD. The price is what one unit of it costs in the quote currency.', lesson: 'currency-pair-base-quote' },
    { id: 'quote-currency', term: 'Quote currency', text: 'The second currency in a pair, such as USD in EUR/USD. The price of the pair is in this currency.', lesson: 'currency-pair-base-quote' },
    { id: 'major-pair', term: 'Major pair', text: 'A currency pair of the US dollar with another big currency, such as EUR/USD or USD/JPY. The most traded pairs.', lesson: 'majors-minors-crosses' },
    { id: 'cross-pair', term: 'Cross (minor pair)', text: 'A currency pair without the US dollar, such as EUR/GBP or EUR/JPY.', lesson: 'majors-minors-crosses' },
    { id: 'pip', term: 'Pip', text: 'The usual unit of a forex move: 0.0001 for most pairs, 0.01 for pairs priced in yen. On EUR/USD, one pip on 1 lot is worth $10.', lesson: 'pips-and-pip-value' },
    { id: 'forex-session', term: 'Forex session', text: 'The working hours of a big forex centre: Sydney, Tokyo, London or New York. The Market pulse shows which are open.', lesson: 'forex-sessions' },
    { id: 'central-bank', term: 'Central bank', text: 'The institution that sets a currency’s main interest rate, such as the Federal Reserve for the dollar or the European Central Bank for the euro.', lesson: 'rates-and-central-banks' },
    { id: 'interest-rate-differential', term: 'Interest rate differential', text: 'The gap between the interest rates of the two currencies in a pair. It drives carry.', lesson: 'rates-and-central-banks' },
    { id: 'carry', term: 'Carry', text: 'What holding one currency against another earns or costs each day, from the gap in their interest rates. Here it shows up as swap.', lesson: 'carry-and-swap' },
];
//# sourceMappingURL=forex.js.map